<template>
  <div class="el-main">
    <EntrySheetEduHistoryCard
      :basic-data="basicData"
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
      :active-history-item="activeHistoryItem"
      :educational-reset-visible="educationalResetDialogVisible"
      :description-max-length="descriptionMaxLength"
      @set-educational-backgrounds="setEducationalBackgrounds"
      @update:educational-reset-visible="educationalResetDialogVisible = $event"
      @reset-educational-backgrounds="resetEducationalBackgrounds"
      @add-educational-history="addEducationalHistory"
      @delete-educational-history="deleteEducationalHistory"
      @move-educational-history="moveEducationalHistory"
      @activate-history-item="activateHistoryItem"
    />

    <EntrySheetTextFieldCard
      card-id="study-history"
      :current-card-id="currentCardId"
      title="ゼミ・研究を入力"
      description="100字程度で大学でのゼミ活動・研究についてまとめましょう"
      field-key="studyHistory"
      error-key="studyHistory"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
    />

    <EntrySheetTextFieldCard
      card-id="club-activity"
      :current-card-id="currentCardId"
      title="クラブ・サークルを入力"
      description="100字程度でクラブ・サークルでの活動についてまとめましょう"
      field-key="clubActivity"
      error-key="clubActivity"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
    />

    <EntrySheetTextFieldCard
      card-id="license-history"
      :current-card-id="currentCardId"
      title="保有資格を入力"
      description="保有資格を入力しよう"
      field-key="licenseHistory"
      error-key="licenseHistory"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
      input-class="formht"
    />

    <EntrySheetTextFieldCard
      card-id="skill-list"
      :current-card-id="currentCardId"
      title="趣味・特技を入力"
      description="趣味・特技を入力してアピールしましょう"
      field-key="skillList"
      error-key="skillList"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
      input-class="formht"
    />

    <EntrySheetTextFieldCard
      card-id="parttime-job"
      :current-card-id="currentCardId"
      title="アルバイト経験を入力"
      description="100字程度でアルバイトでの経験についてまとめましょう"
      field-key="parttimeJob"
      error-key="parttimeJob"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
    />

    <EntrySheetTextFieldCard
      card-id="reasonNew"
      :current-card-id="currentCardId"
      title="自己PRを入力"
      description="100字程度で自己PRを書きましょう"
      field-key="reasonNew"
      error-key="reasonNew"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
    />

    <EntrySheetTextFieldCard
      card-id="reason"
      :current-card-id="currentCardId"
      title="志望動機を入力"
      description="100字程度で志望動機を書きましょう"
      field-key="reason"
      error-key="reason"
      :preview-errors="previewErrors"
      :textarea-min-rows="textareaMinRows"
    />

    <EntrySheetCreateDateCard :current-card-id="currentCardId" />

    <EntrySheetConfirmCard :current-card-id="currentCardId" />

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <CompletedOutputPanel
        :formatId="formatId"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyEntrySheetFormatData,
  provideEntrySheetFormatData,
  useEntrySheetHistories
} from '@/composables/format-edit/useEntrySheetHistories'
import EntrySheetEduHistoryCard from '@/components/format-edit/entry-sheet/EntrySheetEduHistoryCard.vue'
import EntrySheetTextFieldCard from '@/components/format-edit/entry-sheet/EntrySheetTextFieldCard.vue'
import EntrySheetCreateDateCard from '@/components/format-edit/entry-sheet/EntrySheetCreateDateCard.vue'
import EntrySheetConfirmCard from '@/components/format-edit/entry-sheet/EntrySheetConfirmCard.vue'
import CompletedOutputPanel from '@/components/format-edit/completed/CompletedOutputPanel.vue'

const props = defineProps<{
  basicData: Record<string, unknown>
  formatId?: string
  previewFormat?: { errorMessages?: Record<string, string> } | null
  currentCardId?: string
  exampleSentences?: Record<string, unknown>
}>()

const { formLib } = useHostServices()

const { formatData } = useFormatLocalData({
  saveName: 'entrysheet',
  saveOtherName: 'shokumukeirekisho',
  createEmpty: createEmptyEntrySheetFormatData,
  // このテンプレートには配偶者選択UIがないため、ロード時に null に固定する
  migrate: (d) => ({
    ...d,
    commuteDescription: '',
    commuteHours: '',
    commuteMinutes: '',
    spouseNumber: '',
    hasSpouse: null,
    underDutyToSupport: null,
    foreignStudent: false
  })
})
provideEntrySheetFormatData(formatData)

const {
  activeHistoryItem,
  educationalResetDialogVisible,
  setEducationalBackgrounds,
  resetEducationalBackgrounds,
  addEducationalHistory,
  deleteEducationalHistory,
  moveEducationalHistory,
  activateHistoryItem
} = useEntrySheetHistories(formatData)

const descriptionMaxLength = 80
const textareaMinRows = 3
const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)

const previewErrors = computed(() => {
  if (!props.previewFormat || !props.previewFormat.errorMessages) return {}
  return props.previewFormat.errorMessages
})

const getMode = computed(() => process.env.VUE_APP_MODE)

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

watch(
  () => formatData.createDate,
  (newValue) => {
    if (newValue == null) return
    formatData.writeDate = formLib.dateFormat(
      newValue as Date,
      'yyyy年 m月 d日'
    )
  }
)

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  formatData.registYagioffer = true
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
})
</script>

<style lang="scss" scoped>
* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.card-header h1,
.card-header h2 {
  padding-bottom: 1.5rem;
}
</style>
